import {
  Table,
  TableBody,
  TableRow,
  TableContainer,
  TableCell,
  Box,
  Grid,
  IconButton,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useCallback } from 'react';

import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import ClearIcon from '../../../../packages/common/shared-ui/src/icons/ClearIcon';
import DoneIcon from '../../../../packages/common/shared-ui/src/icons/DoneIcon';
import PlusIcon from '../../../../packages/common/shared-ui/src/icons/PlusIcon';
import ReportDeleteIcon from '../../../../packages/common/shared-ui/src/icons/ReportDeleteIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: tableSettings
const StyledTableSettings = styled(Table)(({ theme }) => ({
  backgroundColor: '#ffffff',

  '& .MuiTableCell-root': {
    borderBottom: '1px solid #e4e4ef',
    borderTop: 0,
    padding: 0,
  },

  '& .MuiTableBody-root td:first-of-type': {
    borderRight: '1px solid #e4e4ef',
    paddingLeft: theme.spacing(3),
  },

  '& .MuiTableBody-root td:last-child': {
    borderLeft: '1px solid #e4e4ef',
  },

  '& .MuiTableBody-root': {
    borderBottom: '1px solid #e4e4ef',
    borderTop: '1px solid #e4e4ef',
  },
}));
// Original class: tableRow
const StyledTableRow = styled(TableRow)(theme => ({
  '&.MuiTableRow-root': {
    height: '40px',
  },
}));
// Original class: iconButton
const StyledIconButton = styled(IconButton)(theme => ({
  '&.MuiButtonBase-root.MuiIconButton-root': {
    minWidth: '32px',
    padding: '0px',
  },
}));
// Original class: selectTracker
const StyledSelectTracker = styled(TableRow)(theme => ({
  '&.MuiTableRow-root td:first-of-type': {
    paddingLeft: 0,
  },
}));
// Original class: groupIcon
const StyledGroupIcon = styled(Box)(theme => ({
  '& .MuiSvgIcon-root.MuiSvgIcon-fontSizeSmall': {
    right: 10,
  },
}));

// Original class: createSelect
const StyledCreateSelect = styled(Select)(({ theme }) => ({
  '&.MuiInputBase-root.Mui-disabled': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E4E4EF',
    },
    opacity: '70%',
  },

  border: '16px',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
}));
// Original class: menuItem
const StyledMenuItem = styled(MenuItem)(theme => ({
  '&.Mui-selected': {
    background: '#DCDFF4 !important',
  },

  '& .MuiTypography-root': {
    textTransform: 'unset',
    color: '#212346',
  },
}));

const ResplanSettingsTracker = ({ settings, setSettings, changed }) => {
  const getLocalString = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [trackers, setTrackers] = useState(
    settings.trackers.map(t => ({ ...t, isNew: false })),
  );
  const [selectedTracker, setSelectedTracker] = useState('');
  const [shouldOpenSelect, setShouldOpenSelect] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
    setShouldOpenSelect(true);
  }, []);

  const handleClose = useCallback(() => {
    setShouldOpenSelect(false);
  }, []);

  const handleSelectOpen = useCallback(() => {
    if (isOpen) {
      setShouldOpenSelect(true);
    }
  }, [isOpen]);

  const handleChange = event => {
    const newTrackers = trackers.map(tracker =>
      tracker.valueId === event.target.value
        ? { ...tracker, isNew: true }
        : tracker,
    );
    setTrackers(newTrackers);
    setSelectedTracker(event.target.value);
    changed();
  };
  const handleDone = () => {
    const newTrackers = trackers
      .map(tracker =>
        tracker.valueId == selectedTracker
          ? { ...tracker, isChecked: true, isNew: false }
          : tracker,
      )
      .sort((a, b) => (a.isChecked === b.isChecked ? 0 : a.isChecked ? 1 : -1));
    setTrackers(newTrackers);
    setSettings(prevSettings => ({
      ...prevSettings,
      trackers: prevSettings.trackers.map(tracker =>
        tracker.valueId === selectedTracker
          ? { ...tracker, isChecked: true }
          : tracker,
      ),
    }));
    handleOpen();
    setSelectedTracker('');
    changed();
  };

  const handleDelete = valueId => {
    const newTrackers = trackers.map(tracker =>
      tracker.valueId == valueId ? { ...tracker, isChecked: false } : tracker,
    );
    setTrackers(newTrackers);
    setSettings(prevSettings => ({
      ...prevSettings,
      trackers: prevSettings.trackers.map(tracker =>
        tracker.valueId === valueId
          ? { ...tracker, isChecked: false }
          : tracker,
      ),
    }));
    setSelectedTracker('');
    changed();
  };

  const handleCancel = () => {
    const newTrackers = trackers.map(tracker =>
      tracker.isNew == true ? { ...tracker, isNew: false } : tracker,
    );
    setTrackers(newTrackers);
    handleOpen();
    setSelectedTracker('');
    changed();
  };
  return (
    <>
      <Typography
        variant='h4'
        sx={{
          marginLeft: 3,
          fontWeight: 700,
          color: '#082253',
        }}
      >
        {getLocalString('AdminResourcePlan', 'labels', 'trackerHeader')}
      </Typography>
      <Grid
        sx={{
          marginTop: 0,
        }}
      >
        <TableContainer>
          <StyledTableSettings>
            <TableBody>
              {trackers
                .filter(tracker => tracker.isChecked)
                .sort((a, b) =>
                  a.isChecked === b.isChecked ? 0 : a.isChecked ? 1 : -1,
                )
                .map(tracker => (
                  <StyledTableRow key={tracker.valueId}>
                    {!isOpen ? (
                      <>
                        <TableCell width='90%' align='left'>
                          {tracker.valueName}
                        </TableCell>
                        <TableCell align='center'>
                          <StyledIconButton
                            disableRipple
                            onClick={() => handleDelete(tracker.valueId)}
                          >
                            <ReportDeleteIcon
                              width='24'
                              height='24'
                              viewBox='0 0 24 24'
                            />
                          </StyledIconButton>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell width='80%' colSpan={2} align='left'>
                          {tracker.valueName}
                        </TableCell>
                        <TableCell colSpan={1} align='center'>
                          <StyledIconButton
                            disableRipple
                            onClick={() => handleDelete(tracker.valueId)}
                          >
                            <ReportDeleteIcon
                              width='24'
                              height='24'
                              viewBox='0 0 24 24'
                            />
                          </StyledIconButton>
                        </TableCell>
                      </>
                    )}
                  </StyledTableRow>
                ))}
              {isOpen && (
                <StyledSelectTracker>
                  <TableCell width='80%' align='left'>
                    <StyledCreateSelect
                      fullWidth
                      open={isOpen && shouldOpenSelect}
                      onOpen={handleSelectOpen}
                      onClose={handleClose}
                      IconComponent={() => (
                        <StyledGroupIcon>
                          <ChevronIcon
                            direction='down'
                            fontSize='small'
                            viewBox='0 0 24 24'
                          />
                        </StyledGroupIcon>
                      )}
                      MenuProps={{
                        slotProps: {
                          paper: {
                            sx: {
                              borderRadius: '16px',
                              maxHeight: '40vh',
                              overflow: 'auto',
                            },
                          },
                        },
                      }}
                      variant='standard'
                      disableUnderline
                      value={selectedTracker || 'choose'}
                      onChange={handleChange}
                    >
                      <MenuItem disabled value='choose'>
                        <Typography
                          variant='h4'
                          sx={{
                            color: '#212346',
                          }}
                        >
                          {getLocalString('labels', 'select')}
                        </Typography>
                      </MenuItem>
                      {trackers
                        .filter(tracker => !tracker.isChecked)
                        .sort((a, b) => b.isNew - a.isNew)
                        .map(tracker => (
                          <StyledMenuItem
                            key={tracker.valueId}
                            value={tracker.valueId}
                          >
                            <Typography
                              variant='h4'
                              sx={{
                                color: '#212346',
                              }}
                            >
                              {tracker.valueName}
                            </Typography>
                          </StyledMenuItem>
                        ))}
                    </StyledCreateSelect>
                  </TableCell>
                  <TableCell align='center' width='10%'>
                    <StyledIconButton disableRipple onClick={handleDone}>
                      <DoneIcon
                        color='#37C670'
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                      />
                    </StyledIconButton>
                  </TableCell>
                  <TableCell align='center' width='10%'>
                    <StyledIconButton disableRipple onClick={handleCancel}>
                      <ReportDeleteIcon
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                      />
                    </StyledIconButton>
                  </TableCell>
                </StyledSelectTracker>
              )}
            </TableBody>
          </StyledTableSettings>
        </TableContainer>
        <Box
          sx={{
            marginLeft: 1,
            marginTop: 2,
          }}
        >
          {!isOpen ? (
            <IconButton disableRipple onClick={handleOpen}>
              <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
            </IconButton>
          ) : (
            <IconButton disabled disableRipple>
              <PlusIcon
                color='#DCDFF4'
                width='24'
                height='24'
                viewBox='0 0 24 24'
              />
            </IconButton>
          )}
        </Box>
      </Grid>
    </>
  );
};

export default ResplanSettingsTracker;
